import { emptyMeta, MetaBonuses } from '../sim/stats'

const SAVE_KEY = 'whackhalla.save.v1'

export interface MetaState {
  bank: number // unspent gold
  upgrades: Record<string, number> // permanent upgrade id -> level
  unlockedCharacters: string[]
  unlockedMaps: string[]
  // lifetime / record stats
  totalGoldEarned: number
  totalKills: number
  totalRuns: number
  bestTime: number
  bestLevel: number
  bossesKilled: number
  clearedMaps: string[]
  bestTier: Record<string, number> // map id -> highest tier cleared (0 = none)
  bestCurse: Record<string, number> // map id -> highest curse cleared (0 = none)
  achievements: string[]
  challengesCompleted: string[] // challenge ids beaten (one-time reward each)
  disabledItems: string[] // item ids excluded from the draft pool (anti-dilution)
  evolvedTotal: number
  settings: { master: number; sfx: number; music: number; screenShake: boolean; damageNumbers: boolean }
}

export function defaultMeta(): MetaState {
  return {
    bank: 0,
    upgrades: {},
    unlockedCharacters: ['bonker', 'pip', 'tank'],
    unlockedMaps: ['meadow'],
    totalGoldEarned: 0,
    totalKills: 0,
    totalRuns: 0,
    bestTime: 0,
    bestLevel: 0,
    bossesKilled: 0,
    clearedMaps: [],
    bestTier: {},
    bestCurse: {},
    achievements: [],
    challengesCompleted: [],
    disabledItems: [],
    evolvedTotal: 0,
    settings: { master: 0.8, sfx: 0.9, music: 0.5, screenShake: true, damageNumbers: true },
  }
}

export interface MetaUpgradeDef {
  id: string
  name: string
  icon: string
  maxLevel: number
  desc: (level: number) => string
  cost: (nextLevel: number) => number
  apply: (m: MetaBonuses, level: number) => void
}

export const META_UPGRADES: MetaUpgradeDef[] = [
  {
    id: 'might',
    name: 'Might',
    icon: '💪',
    maxLevel: 10,
    desc: (l) => `+${5 * l}% damage on every run.`,
    cost: (n) => 40 * n,
    apply: (m, l) => {
      m.damage += 0.05 * l
    },
  },
  {
    id: 'vigor',
    name: 'Vigor',
    icon: '❤️',
    maxLevel: 10,
    desc: (l) => `+${15 * l} max HP on every run.`,
    cost: (n) => 35 * n,
    apply: (m, l) => {
      m.maxHp += 15 * l
    },
  },
  {
    id: 'haste',
    name: 'Haste',
    icon: '🏃',
    maxLevel: 8,
    desc: (l) => `+${3 * l}% move speed on every run.`,
    cost: (n) => 45 * n,
    apply: (m, l) => {
      m.moveSpeed += 0.03 * l
    },
  },
  {
    id: 'frenzy',
    name: 'Frenzy',
    icon: '⚔️',
    maxLevel: 8,
    desc: (l) => `+${4 * l}% attack speed on every run.`,
    cost: (n) => 55 * n,
    apply: (m, l) => {
      m.attackSpeed += 0.04 * l
    },
  },
  {
    id: 'fortune',
    name: 'Fortune',
    icon: '🍀',
    maxLevel: 6,
    desc: (l) => `+${0.5 * l} luck on every run.`,
    cost: (n) => 70 * n,
    apply: (m, l) => {
      m.luck += 0.5 * l
    },
  },
  {
    id: 'scholar',
    name: 'Scholar',
    icon: '🎓',
    maxLevel: 6,
    desc: (l) => `+${6 * l}% XP gain on every run.`,
    cost: (n) => 60 * n,
    apply: (m, l) => {
      m.xpGain += 0.06 * l
    },
  },
  {
    id: 'greed',
    name: 'Greed',
    icon: '🪙',
    maxLevel: 6,
    desc: (l) => `+${8 * l}% gold gain on every run.`,
    cost: (n) => 50 * n,
    apply: (m, l) => {
      m.goldGain += 0.08 * l
    },
  },
  {
    id: 'plating',
    name: 'Plating',
    icon: '🛡️',
    maxLevel: 6,
    desc: (l) => `+${2 * l} armor on every run.`,
    cost: (n) => 50 * n,
    apply: (m, l) => {
      m.armor += 2 * l
    },
  },
  {
    id: 'magnetism',
    name: 'Magnetism',
    icon: '🧲',
    maxLevel: 5,
    desc: (l) => `+${10 * l}% pickup range on every run.`,
    cost: (n) => 40 * n,
    apply: (m, l) => {
      m.pickupRange += 0.1 * l
    },
  },
  {
    id: 'precision',
    name: 'Precision',
    icon: '🎯',
    maxLevel: 6,
    desc: (l) => `+${2 * l}% crit chance on every run.`,
    cost: (n) => 65 * n,
    apply: (m, l) => {
      m.critChance += 0.02 * l
    },
  },
  {
    id: 'second_wind',
    name: 'Second Wind',
    icon: '🪽',
    maxLevel: 1,
    desc: () => `Revive once per run with 50% HP.`,
    cost: () => 400,
    apply: (m, l) => {
      m.revives += l
    },
  },
  // PT4 — breadth: new permanent levers for longer meta-progression
  {
    id: 'ferocity',
    name: 'Ferocity',
    icon: '🔥',
    maxLevel: 6,
    desc: (l) => `+${(0.1 * l).toFixed(1)} crit damage on every run.`,
    cost: (n) => 75 * n,
    apply: (m, l) => {
      m.critMult += 0.1 * l
    },
  },
  {
    id: 'reach',
    name: 'Reach',
    icon: '🌐',
    maxLevel: 6,
    desc: (l) => `+${4 * l}% area of effect on every run.`,
    cost: (n) => 55 * n,
    apply: (m, l) => {
      m.area += 0.04 * l
    },
  },
  {
    id: 'velocity',
    name: 'Velocity',
    icon: '🚀',
    maxLevel: 6,
    desc: (l) => `+${5 * l}% projectile speed on every run.`,
    cost: (n) => 45 * n,
    apply: (m, l) => {
      m.projectileSpeed += 0.05 * l
    },
  },
  {
    id: 'vitality',
    name: 'Vitality',
    icon: '➕',
    maxLevel: 6,
    desc: (l) => `+${(0.5 * l).toFixed(1)} HP regen/sec on every run.`,
    cost: (n) => 60 * n,
    apply: (m, l) => {
      m.hpRegen += 0.5 * l
    },
  },
  {
    id: 'leech',
    name: 'Leech',
    icon: '🩸',
    maxLevel: 5,
    desc: (l) => `+${l}% lifesteal on every run.`,
    cost: (n) => 90 * n,
    apply: (m, l) => {
      m.lifesteal += 0.01 * l
    },
  },
  {
    id: 'volley',
    name: 'Volley',
    icon: '🎯',
    maxLevel: 2,
    desc: (l) => `+${l} projectile on every run.`,
    cost: (n) => 250 * n,
    apply: (m, l) => {
      m.projectileCount += l
    },
  },
]

export const META_UPGRADE_MAP: Record<string, MetaUpgradeDef> = {}
for (const u of META_UPGRADES) META_UPGRADE_MAP[u.id] = u

export function computeMetaBonuses(meta: MetaState): MetaBonuses {
  const m = emptyMeta()
  for (const u of META_UPGRADES) {
    const lvl = meta.upgrades[u.id] ?? 0
    if (lvl > 0) u.apply(m, lvl)
  }
  return m
}

export function upgradeCost(id: string, currentLevel: number): number | null {
  const def = META_UPGRADE_MAP[id]
  if (!def) return null
  if (currentLevel >= def.maxLevel) return null
  return def.cost(currentLevel + 1)
}

export function loadMeta(): MetaState {
  try {
    const raw = localStorage.getItem(SAVE_KEY)
    if (!raw) return defaultMeta()
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return defaultMeta()
    // Defensive per-field merge: a corrupt/partial save (null or wrong-typed field) must NOT crash the game —
    // only accept a parsed field when its type matches the default; objects merge over defaults, numbers must
    // be finite. (Previously only `settings` was guarded, so e.g. {"upgrades":null} hard-crashed the menu.)
    const d = defaultMeta()
    const dr = d as unknown as Record<string, unknown>
    const out: Record<string, unknown> = { ...dr }
    const src = parsed as Record<string, unknown>
    for (const k of Object.keys(dr)) {
      const pv = src[k]
      const dv = dr[k]
      if (pv === undefined || pv === null) continue
      if (Array.isArray(dv)) { if (Array.isArray(pv)) out[k] = pv }
      else if (typeof dv === 'object') { if (typeof pv === 'object' && !Array.isArray(pv)) out[k] = { ...(dv as object), ...(pv as object) } }
      else if (typeof dv === 'number') { if (typeof pv === 'number' && Number.isFinite(pv)) out[k] = pv }
      else if (typeof pv === typeof dv) out[k] = pv
    }
    // unlocked lists must always include the default-unlocked floor (an empty/garbage array would soft-brick run-start)
    const strs = (a: unknown): string[] => (Array.isArray(a) ? a.filter((x) => typeof x === 'string') as string[] : [])
    out.unlockedCharacters = Array.from(new Set([...d.unlockedCharacters, ...strs(out.unlockedCharacters)]))
    out.unlockedMaps = Array.from(new Set([...d.unlockedMaps, ...strs(out.unlockedMaps)]))
    // number-map fields must hold only finite numbers (a string value would break tier gating / Math.max)
    for (const mk of ['upgrades', 'bestTier', 'bestCurse']) {
      const m = out[mk] as Record<string, unknown>
      if (m && typeof m === 'object') for (const k of Object.keys(m)) { const v = m[k]; if (typeof v !== 'number' || !Number.isFinite(v)) delete m[k] }
    }
    return out as unknown as MetaState
  } catch {
    return defaultMeta()
  }
}

export function saveMeta(meta: MetaState): void {
  try {
    localStorage.setItem(SAVE_KEY, JSON.stringify(meta))
  } catch {
    // storage unavailable — ignore
  }
}

export function resetMeta(): MetaState {
  const m = defaultMeta()
  saveMeta(m)
  return m
}
